import { useDebounce, usePrevious, useQueryState } from 'commons/utils';
import { SearchCategories } from 'components/mediaLibrary/constants';
import { SearchPagination } from 'components/search/search-pagination';
import { Spinner } from 'components/spinner';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { IState } from 'reducers/reducers';
import { languageState } from 'redux-recoil-sync.state';
import { Checkbox, Dimmer, Loader } from 'semantic-ui-react';
import { getView, toggleView, View } from '../blockUtils';
import BlockList from './BlockList';
import { GoBackToLocaitonState } from './Blocks.types';
import { BlockCards } from './components/BlockCards';
import { BlocksHeader } from './components/BlocksHeader';
import { BlocksTable } from './components/BlocksTable';
import useBlockParams from './UseBlockParams';

interface OwnProps {
  useAsPicker?: boolean;
  onClick?: (id: string) => void;
}

const SEARCH_CATEGORIES = [
  SearchCategories.All,
  SearchCategories.Names,
  SearchCategories.Tags,
  SearchCategories.Manuscript,
  SearchCategories.Description,
];

export function BlockOverview(props: OwnProps) {
  const [language] = useRecoilState(languageState);
  const history = useHistory<GoBackToLocaitonState>();
  const [showList, setShowList] = useState(getView() === View.LIST);
  const [searchTerm, setSearchTerm] = useQueryState<string>('query', '');
  const backgroundState = history.location.state?.goBackTo;
  const isInBackground = !!backgroundState;
  const [searchCategory, setSearchCategory] = useQueryState<SearchCategories>('category', SearchCategories.All); // use All as default

  const wasInBackground = usePrevious(isInBackground, true);
  const debouncedSearchTerm = useDebounce(searchTerm, wasInBackground ? 0 : 750);

  const [page, setPage] = useQueryState<number>('page', 1); // start on page 1
  const [showArchived, setShowArchived] = useQueryState<boolean>('showArchived', false);

  const useQuery = useBlockParams(page, searchCategory, debouncedSearchTerm, showArchived, isInBackground);
  const blocks: BlockList | undefined = useQuery ? useQuery.data : ({} as BlockList);

  const handleOnClickBlock = (id: string) => {
    if (props.onClick) {
      props.onClick(id);
    } else {
      const { pathname, search, hash } = window.location;
      history.push({
        pathname: `/block/overview/${id}`,
        state: {
          goBackTo: {
            pathname,
            search,
            hash,
          },
        },
      });
    }
  };

  const blockList = (): JSX.Element | null => {
    if (!blocks) {
      return null;
    }
    return showList ? (
      <BlocksTable items={blocks.entries} language={language} onClick={handleOnClickBlock} />
    ) : (
      <BlockCards items={blocks.entries} language={language} onClick={handleOnClickBlock} />
    );
  };

  return (
    <div>
      <UploadingOverlay />
      {!blocks ? null : (
        <div>
          <BlocksHeader
            showList={showList}
            useAsPicker={props.useAsPicker}
            onCreateBlock={() => history.push(`/block/overview/new`)}
            onToogleList={() => {
              setShowList(!showList);
              toggleView();
            }}
          >
            <div>
              <Checkbox
                label="Show archive"
                checked={showArchived}
                onChange={() => setShowArchived(!showArchived)}
                style={{ marginBottom: 10 }}
                toggle
              />
              <SearchPagination
                activePage={page}
                categories={SEARCH_CATEGORIES}
                onCategoryChange={(cat) => {
                  setSearchCategory(cat as SearchCategories);
                  setPage(1);
                }}
                onPageChange={(pageNumber) => setPage(pageNumber)}
                onSearchChange={(term) => {
                  setPage(1);
                  setSearchTerm(term);
                }}
                totalPages={blocks.pages}
                selectedCategory={searchCategory}
                searchTerm={searchTerm}
              />
              {!useQuery.isFetching && <div>Found {`${blocks?.count} blocks - Showing ${blocks?.entries.length} `}</div>}
            </div>
          </BlocksHeader>

          {useQuery.isFetching ? <Spinner visible={useQuery.isFetching} /> : blockList()}
        </div>
      )}
    </div>
  );
}

function UploadingOverlay() {
  const isUploading: boolean = useSelector((state: IState) => state.blocks.isUploading);

  if (!isUploading) return null;
  return (
    <Dimmer active inverted>
      <Loader size="small">Doing some hard work. Please wait</Loader>
    </Dimmer>
  );
}
